import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import Footer from '../../components/Global/Footer/Footer';
import Activities from '../../components/Catalogue/Activities/Activities';
import Header from '../../components/PlacePage/Header/Header';

import Description from '../../components/Global/Description/Description';
import FilteredCarousel from '../../components/PlacePage/Trails/TrailCarousel/Carousel';
import Media from '../../components/PlacePage/Media/Media';
import { Place } from '../../models/Place';
import TrailsOnMap from '../../components/PlacePage/TrailsOnMap/TrailsOnMap';
import { useAppSelector } from '../../state/hooks';
import { RootState } from '../../state/store';
import { shallowEqual } from 'react-redux';
import FeaturedVideo from '../../components/Global/FeaturedVideo/FeaturedVideo';
import { PlaceHelper } from '../../models/helpers/Helpers';
import { uniqBy } from 'lodash';
import { useParams, useHistory } from 'react-router';
import { PlaceFactory, PlaceQuery } from '../../models/factories/PlaceFactory';
import { ITaxonomy } from '../../models/interfaces/ITaxonomy';
import Weather from '../../components/PlacePage/Weather/Weather';
import { ExplorerContext } from '../../application/config';
import PlaceTopNav from '../../components/Global/InnerPageComponents/PlaceTopNav';
import ExternalUrl from '../../components/Global/ExternalUrl/ExternalUrl';
import { selectPrefs } from '../../state/selectors';
import {
	filterTrailsByAccessibility,
	sortTrailsByPreferences,
} from '../../models/helpers/preferenceRanking';

const PlacePage: React.FC = () => {
	// get the placeId route parameter
	const history = useHistory();
	const AppData = useContext(ExplorerContext);
	const { placeId } = useParams<{ placeId: string }>();
	const [ActiveActivities, SetActiveActivities] = useState<ITaxonomy[]>([]);
	const [ActivePlace, SetActivePlace] = useState<Place | undefined>(
		undefined,
	);

	const network_status = useAppSelector(
		(state: RootState) => state.network.status,
		shallowEqual,
	);
	const userPrefs = useAppSelector(selectPrefs, shallowEqual);

	// const tags = poi.tags.map(tag => {name: 'xyz'})
	//

	const getActivities = useCallback(() => {
		// console.log('Place : ', ActivePlace);
		// console.log('Getting activities : ');
		if (ActivePlace !== undefined) {
			let activities = ActivePlace.trails
				.map((trail) => {
					return trail.activities;
				})
				.flat(1);

			return uniqBy(activities, (e) => {
				return e.id;
			});
		} else {
			return [];
		}
	}, [ActivePlace]);

	useEffect(() => {
		if (placeId) {
			const filterQuery: PlaceQuery = {
				filter: { id: [Number(placeId)] },
			};
			const selectedPlaceList = PlaceFactory.filter(
				AppData.features?.all_places ?? PlaceFactory.getEmpty(),
				filterQuery,
			);
			if (selectedPlaceList.places.length > 0) {
				SetActivePlace(selectedPlaceList.places[0]);
			} else {
				SetActivePlace(undefined);
			}
		} else {
			SetActivePlace(undefined);
		}
	}, [placeId, AppData.features?.all_places, getActivities]);

	useEffect(() => {
		const activities = getActivities();
		SetActiveActivities(activities);
	}, [ActivePlace, getActivities]);

	const prioritisedTrails = useMemo(
		() =>
			ActivePlace
				? sortTrailsByPreferences(
					filterTrailsByAccessibility([...ActivePlace.trails], userPrefs),
					userPrefs,
				)
				: [],
		[ActivePlace, userPrefs],
	);

	// console.log(place);
	if (ActivePlace !== undefined) {
		return (
			<IonPage>
				<IonContent fullscreen>
					<PlaceTopNav placeId={ActivePlace.id} />
					<Header
						title={ActivePlace.title}
						image={ActivePlace.image}
						metaString={PlaceHelper.generateMetaString(ActivePlace)}
						topicString={PlaceHelper.generateTopicString(
							ActivePlace,
						)}
					/>
					<main>
						<>
							{ActivePlace.description && (
								<Description text={ActivePlace.description} />
							)}
							{ActivePlace.featured_video && (
								<FeaturedVideo
									src={ActivePlace.featured_video}
								/>
							)}
								{prioritisedTrails.length > 0 && (
									<>
										<FilteredCarousel
											items={prioritisedTrails}
										/>
										<TrailsOnMap trails={prioritisedTrails} />
									</>
								)}

							{ActivePlace.url && (
								<ExternalUrl
									url={ActivePlace.url}
									description={
										ActivePlace.external_url_description
									}
								/>
							)}

							{/* Weather delivered as an extention, if required to pay for an api */}
							{network_status && <Weather place={ActivePlace} />}
							{/* <h2 className="pa3">Weather...?</h2> */}
							<Media place={ActivePlace} />

							{ActiveActivities.length > 0 && (
								<Activities activities={ActiveActivities} />
							)}

							{/* {ActivePlace.tags.length > 0 && (
								<Tags tags={ActivePlace.tags} />
							)} */}
							
						</>
					</main>
					<Footer />
				</IonContent>
			</IonPage>
		);
	} else {
		return (
			<IonPage>
				<IonContent fullscreen>
					<main style={{ padding: '2rem', textAlign: 'center' }}>
						<h2>Place not found</h2>
						<p>This place may have been removed or is no longer available.</p>
						<button onClick={() => history.replace('/home')}>Go Home</button>
					</main>
				</IonContent>
			</IonPage>
		);
	}
};

export default PlacePage;
